// Adapted from https://usehooks-ts.com/react-hook/use-media-query

import { useLayoutEffect } from '@react-aria/utils';
import { useState } from 'react';

type UseMediaQueryOptions = {
  defaultValue?: boolean;
  initializeWithValue?: boolean;
};

const IS_SERVER = typeof window === 'undefined';

const getMatches = (query: string, defaultValue: boolean = false): boolean => {
  if (IS_SERVER) {
    return defaultValue;
  }

  return window.matchMedia(query).matches;
};

/** @internal */
export function useMediaQuery(
  query: string,
  { defaultValue = false, initializeWithValue = true }: UseMediaQueryOptions = {},
): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (initializeWithValue) {
      return getMatches(query, defaultValue);
    }

    return defaultValue;
  });

  useLayoutEffect(() => {
    function handleChange() {
      setMatches(getMatches(query, defaultValue));
    }

    const matchMedia = window.matchMedia(query);

    handleChange();

    matchMedia.addEventListener('change', handleChange);

    return () => {
      matchMedia.removeEventListener('change', handleChange);
    };
  }, [query, defaultValue]);

  return matches;
}
